// Third-parties
import round from 'lodash/round';

// Utils
import { deepClone } from '../../../../utils/objects';
import { formatLocalizedString, formatCurrency } from '../../../../utils/i18n';

// Enums
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';

// Components
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';
import { NotApplicableCell } from '../../../../views/components/cells';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import Dictionary from '@/types/dictionary';
import ComparisonHolding from '@/types/comparison-holding';
import LocalizedString from '@/types/localized-string';

/**
 * @class PortfolioYieldDAO Data Access Object
 */
export default class PortfolioYieldDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new PortfolioYieldDAO(proposal, proposalElement);
  }

  totalYieldTotals: boolean = true;

  totalYieldSubTotals: boolean = false;

  weightedTotalYieldTotal: number = 0;

  weightedTotalContributionTotal: number = 0;

  weightedTotalYieldTotalForAssetClass: number = 0;

  weightedTotalContributionTotalForAssetClass: number = 0;

  protected getColorKeys(): string[] {
    return ['DEFAULT'];
  }

  protected build(): void {
    let showAmount: boolean = false;
    let groupByAssetType: boolean = true;

    let fieldAsterisks: string[] = [];

    const daoConfig: ProposalNS.PortfolioYieldConfig = this.proposalElement.DAO as ProposalNS.PortfolioYieldConfig;

    if (daoConfig.groupByAssetType !== undefined) {
      groupByAssetType = daoConfig.groupByAssetType;
    }

    if (daoConfig.totalYieldTotals !== undefined) {
      this.totalYieldTotals = daoConfig.totalYieldTotals;
    }

    if (daoConfig.totalYieldSubTotals !== undefined) {
      this.totalYieldSubTotals = daoConfig.totalYieldSubTotals;
    }

    if (daoConfig.extraColumnsDAOOptions !== undefined) {
      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('AMOUNT') !== -1) {
        showAmount = true;
      }
    }

    if (daoConfig.fieldAsterisks !== undefined) {
      fieldAsterisks = daoConfig.fieldAsterisks || [];
    }

    if (this.customFieldNames != null) {
      for (const name of this.customFieldNames.names) {
        this.addFieldName(this.formatLocalizedString(name), ProposalStyleClassNames.ProposalTableContent);
      }
    }

    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      let dpResponse: DataPointResponse<Dictionary<ComparisonHolding[]>> | undefined;
      if (groupByAssetType) {
        dpResponse = DataPoints.getHoldingsByAssetGroup(itemDAO.item);
      } else {
        const localResponse: DataPointResponse<ComparisonHolding[]> = DataPoints.getItemHoldings(itemDAO.item);
        dpResponse = { response: { key: localResponse.response }, disclaimerKeys: localResponse.disclaimerKeys };
      }

      const keys: string[] = Object.keys(dpResponse.response);

      this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.YIELD_AS_AT], itemDAO.item);

      let grandTotalAmount: number = 0;

      // If the asset class column is rendered, the weight field is the second <td /> in the row (index 1)
      // else it's the first <td /> in the row (index 0)
      const totalWeightFieldIndex = 0;

      // If the asset class column is rendered, the total amount field is the third <td /> in the row (index 2)
      // else it's the second <td /> in the row (index 2)
      const totalAmountFieldIndex = 0;

      for (const key of keys) {
        let weightTotal: number = 0;
        let amountTotal: number = 0;
        let rowIndex = 0;

        this.weightedTotalYieldTotalForAssetClass = 0;
        this.weightedTotalContributionTotalForAssetClass = 0;

        const comparisonHoldings = dpResponse.response[key].slice();
        // Sub-group items by their classification
        comparisonHoldings.sort((a, b) => {
          const formatted = [formatLocalizedString(a.classification), formatLocalizedString(b.classification)];
          return formatted[0].localeCompare(formatted[1]);
        });

        for (const comparisonHolding of comparisonHoldings) {
          let index: number = 0;

          const classes = [ProposalStyleClassNames.ProposalTableContent] as string[];
          classes.push(rowIndex % 2 ? ProposalStyleClassNames.ProposalOdd : ProposalStyleClassNames.ProposalEven);

          this.addField(comparisonHolding.name, classes);

          const dollars = round(((itemDAO.item.size || 0) * comparisonHolding.weight) / 100, 4);
          if (showAmount) {
            this.addDollarsValue(itemDAO, dollars, index++, 2, classes);
          }

          weightTotal += comparisonHolding.weight;
          amountTotal += dollars;

          index = this.addYield(itemDAO, comparisonHolding, dollars, index, classes);

          rowIndex++;
        }

        // do not show the total if there is no holdings
        if (rowIndex > 0 && groupByAssetType) {
          let fieldName = this.formatMessage(`Proposal.Totals.${key}`);
          if (fieldAsterisks.includes(key)) {
            fieldName = `*${fieldName}`;
          }

          this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableTotal);
          const field: ProposalFieldDAO = new ProposalFieldDAO(key, '', ProposalStyleClassNames.ProposalTableTotal);
          this.padItem(itemDAO, field);

          // Add the total
          const totalWeightField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalWeightFieldIndex)[itemDAO.getFieldDAOs(totalWeightFieldIndex).length - 1];
          totalWeightField.value = this.formatPercentage(weightTotal, 1);

          if (showAmount) {
            const totalAmountField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalAmountFieldIndex)[itemDAO.getFieldDAOs(totalAmountFieldIndex).length - 1];
            totalAmountField.value = formatCurrency(amountTotal, 2, undefined, false, this.locale);
          }

          if (this.totalYieldSubTotals) {
            const weightedTotalYieldField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalAmountFieldIndex + 3)[
              itemDAO.getFieldDAOs(totalAmountFieldIndex + 3).length - 1
            ];
            weightedTotalYieldField.value = this.formatPercentage(this.weightedTotalYieldTotalForAssetClass, 2);

            const weightedTotalContributionTotalField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalAmountFieldIndex + 4)[
              itemDAO.getFieldDAOs(totalAmountFieldIndex + 4).length - 1
            ];
            weightedTotalContributionTotalField.value = formatCurrency(this.weightedTotalContributionTotalForAssetClass, 0, undefined, false, this.locale);
          }
        }

        grandTotalAmount += amountTotal;
      }

      // Add the grand Total
      const grandTotalLabel = this.formatMessage('Proposal.Totals.Portfolio');
      const grandTotalClassName = `${ProposalStyleClassNames.ProposalTableTotal} ${ProposalStyleClassNames.ProposalTableGrandTotal}`;
      this.addFieldName(grandTotalLabel, grandTotalClassName);

      const grandTotalField: ProposalFieldDAO = new ProposalFieldDAO(grandTotalLabel, '', grandTotalClassName);
      this.padItem(itemDAO, grandTotalField);

      if (showAmount) {
        const grandTotalAmountField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalAmountFieldIndex)[itemDAO.getFieldDAOs(totalAmountFieldIndex).length - 1];
        grandTotalAmountField.value = formatCurrency(grandTotalAmount, 2, undefined, false, this.locale);
      }

      if (this.totalYieldTotals) {
        const weightedTotalYieldField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalAmountFieldIndex + 3)[
          itemDAO.getFieldDAOs(totalAmountFieldIndex + 3).length - 1
        ];
        weightedTotalYieldField.value = this.formatPercentage(this.weightedTotalYieldTotal, 2);

        const weightedTotalContributionTotalField: ProposalFieldDAO = itemDAO.getFieldDAOs(totalAmountFieldIndex + 4)[
          itemDAO.getFieldDAOs(totalAmountFieldIndex + 4).length - 1
        ];
        weightedTotalContributionTotalField.value = formatCurrency(this.weightedTotalContributionTotal, 0, undefined, false, this.locale);
      }
    }
  }

  // private Methods
  addYield(itemDAO: ComparisonItemDAO, comparisonHolding: ComparisonHolding, amount: number, index: number, classes: string[] = []): number {
    const dpResponse: DataPointResponse<Dictionary<number | undefined>> = DataPoints.getComparisonHoldingYield(comparisonHolding);
    const yieldDetails: Dictionary<number | undefined> = dpResponse.response;

    let localIndex = index;
    const columnKeys = ['distributionYield', 'yieldToMaturity', 'totalYield'];
    for (const key of columnKeys) {
      const rawYield: number | undefined = yieldDetails[key];
      if (rawYield !== undefined) {
        if (key === 'totalYield' && this.totalYieldTotals) {
          const yieldAmount = (comparisonHolding.weight * rawYield) / 100;
          this.weightedTotalYieldTotal += yieldAmount;
          this.weightedTotalYieldTotalForAssetClass += yieldAmount;
        }
        this.addGrowthValue(itemDAO, rawYield, localIndex, classes);
      } else {
        this.addNACell(itemDAO, localIndex, classes);
      }
      localIndex++;
    }
    if (yieldDetails.totalYield) {
      const weightedYieldContribution = amount * (yieldDetails.totalYield / 100);
      this.addDollarsValue(itemDAO, weightedYieldContribution, localIndex++, 0);
      if (this.totalYieldTotals) {
        this.weightedTotalContributionTotal += weightedYieldContribution;
        this.weightedTotalContributionTotalForAssetClass += weightedYieldContribution;
      }
    } else {
      this.addNACell(itemDAO, localIndex++);
    }
    return localIndex;
  }

  addField(rawString: LocalizedString | undefined, classes: string[] = []) {
    if (rawString !== undefined) {
      const str: string = formatLocalizedString(rawString, this.locale);
      this.addFieldName(str, this.mergeClasses(classes));
    }
  }

  addStringValue(itemDAO: ComparisonItemDAO, rawString: LocalizedString | undefined, index: number, classes: string[] = []) {
    let str: string = '';
    if (rawString !== undefined) {
      str = formatLocalizedString(rawString, this.locale);
    }
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO(str, str, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addPercentValue(itemDAO: ComparisonItemDAO, value: number, index: number, decimals: number = 2, classes: string[] = []) {
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', this.formatPercentage(value, decimals), this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  addDollarsValue(itemDAO: ComparisonItemDAO, value?: number, index?: number, decimals: number = 2, classes: string[] = []) {
    let strValue = '';
    if (value != null) {
      strValue = formatCurrency(value, decimals, undefined, false, this.locale);
    }

    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', strValue, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addGrowthValue(itemDAO: ComparisonItemDAO, value: number, index: number, classes: string[] = []) {
    if (value > 0) {
      classes.push('positive');
    } else if (value < 0) {
      classes.push('negative');
    }

    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', this.formatPercentage(value, 2), this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addNACell(itemDAO: ComparisonItemDAO, index: number, classes: string[] = []) {
    const naField: ProposalFieldDAO = new ProposalFieldDAO('', '', this.mergeClasses(classes.concat(ProposalStyleClassNames.ProposalNAValue)));
    naField.cellType = NotApplicableCell;
    this.addFieldDAO(itemDAO, naField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  padItem(itemDAO: ComparisonItemDAO, field: ProposalFieldDAO) {
    this.addFieldDAO(itemDAO, field, 0);
    if (this.customColumnNames !== null) {
      for (let index: number = 1; index < this.customColumnNames.columns.length; index++) {
        this.addFieldDAO(itemDAO, deepClone(field), index);
      }
    }
  }

  mergeClasses = (classes: string[]) => classes.join(' ');

  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: any, celltype: any): number {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = celltype;
    this.addFieldDAO(itemDAO, field);
    return value;
  }
}
